<script setup lang="ts">
import {ref, Ref, useTemplateRef, watch} from "vue";
import Translator from "@/utils/common/Translator.ts";
import WidgetContainer from "@/components/EditView/widgets/WidgetContainer.vue";
import AdvancedSearchIcon from "@/assets/widgetIcons/AdvancedSearchIcon.vue";
import ReferenceDialog from "@/components/dialogs/ReferenceDialog.vue";
import {Dialog, QSelect} from "quasar";
import {ReferenceWidgetProps} from "@/utils/presenters/interfaces.ts";
import ReferenceFieldWidgetUtil from "@/utils/editView/widgets/ReferenceFieldWidgetUtil.ts";
import ErrorTooltip from "@/components/EditView/ErrorTooltip.vue";
import LinkIcon from "@/assets/widgetIcons/LinkIcon.vue";
import PlaceUtils from "@/utils/common/PlaceUtils.ts";
import AttachmentLinkUtil from "@/utils/UIConstants/AttachmentLinkUtil.ts";
import DownloadPurpose from "@/utils/constants/DownloadPurpose.ts";
import ImagePreviewer from "@/components/dialogs/ImagePreviewer.vue";
import SearchingAndSuggestionUtil from "@/utils/editView/widgets/SearchingAndSuggestionUtil.ts";
import {useErrorsStore} from "@/stores/ToastDataStore.ts";
import {focusAndSelectInput, type NamaNavigateToNextEmit} from "@/utils/common/NamaVueWidget.ts";
import NamaIcon from "@/components/icons/NamaIcon.vue";
import {useNamaBreakpoints} from "@/composables/useNamaBreakpoints.ts";
import ObjectChecker from "@/utils/common/ObjectChecker.ts";
import EntityReferenceData = namaTypes.EntityReferenceData;
import FieldActionContextInfo = namaTypes.FieldActionContextInfo;

let props = defineProps<ReferenceWidgetProps>();
const fieldId = props.field.id!;
const shouldFilter = ref(false);
const popupVisible = ref(false);
const fieldUtil = new ReferenceFieldWidgetUtil(props.presenter, props.fieldMetaData, props.defaultEntityType);
const formattedValue: Ref<any | null> = fieldUtil.formattedValue();
const loading = ref(false)
const imageVisible = ref(false)
let mouseIn = false;
const mWindow = (window as any);
const options = ref<EntityReferenceData[]>([]);
const input = useTemplateRef<QSelect>("input");
const errorsStore = useErrorsStore();
const emit = defineEmits<NamaNavigateToNextEmit>()

function onBrowseClicked(e: KeyboardEvent | MouseEvent, isInNewMode?: boolean): void {
  if (!fieldUtil.referencedEntityType.value) {
    errorsStore.pushErrorStr("You must select Entity Type");
    e.preventDefault();
    return;
  }
  if ((e as KeyboardEvent).key === "F6" || e.ctrlKey) {
    let fieldValue = fieldUtil.fetchFieldValue();
    const linkToObject = PlaceUtils.linkToObject(fieldValue);
    let url = linkToObject;
    if (ObjectChecker.isEmptyOrNull(linkToObject))
      url = PlaceUtils.linkToPlace(PlaceUtils.linkToNew(fieldUtil.referencedEntityType.value, fieldUtil.presenter.entityType));
    window.open(url, "_blank");
    return;
  }
  Dialog.create({
    component: ReferenceDialog,
    componentProps: {
      fieldUtil,
      field: props.field,
      event: e,
      isInNewMode,
      isFieldEnabled: !fieldUtil.isFieldDisabled(props.forceEnableField)
    }
  });
}

async function filter(val: string | null | undefined) {
  loading.value = true;
  let referencedEntityType = fieldUtil.referencedEntityType.value;
  let contextInfo: FieldActionContextInfo = fieldUtil.presenter.createSearchContextInfo(fieldUtil.fieldId());
  options.value = await fieldUtil.searchFor({
    query: val || "",
    ownerEntityType: fieldUtil.presenter.entityType,
    contextInfo,
    referencedEntityType,
    fieldId: fieldUtil.fieldId(),
    extraCriteria: fieldUtil.presenter.fetchCriteriaFromFilterField(referencedEntityType, fieldId)
  }) ?? [];
  loading.value = false;
}

function onFilter(val, update) {
  update(async () => {
      if (shouldFilter.value)
        await filter(val)
      else
        options.value = [];
    },
    newRef => {
      if (val !== '' && options.value.length > 0) {
        setTimeout(() => {
          newRef.setOptionIndex(-1) // reset optionIndex in case there is something selected
          newRef.moveOptionSelection(1, true) // focus the first selectable option and do not update the input-value
        });
      }
    })
}

function fetchCurrentValue() {
  return fieldUtil.fetchFieldValue() as EntityReferenceData;
}

function openRef(e) {
  const link = PlaceUtils.linkToObject(fetchCurrentValue());
  mWindow.open(link, e.ctrlKey ? '_blank' : '_self');
}

function hasColorOrImg(ref: EntityReferenceData) {
  return SearchingAndSuggestionUtil.shouldShowImgInHeader(fieldUtil.referencedEntityType.value) || (ref && ref.colorCode);
}

function hasImageOrDescriptor() {
  let current = fetchCurrentValue();
  return SearchingAndSuggestionUtil.hasDescriptor(current) || hasColorOrImg(current);
}

function mouseEnter() {
  if (!SearchingAndSuggestionUtil.shouldShowImgInHeader(fieldUtil.referencedEntityType.value))
    return;
  mouseIn = true;
  if (imageVisible.value)
    return;
  setTimeout(() => {
    if (!mouseIn)
      return;
    imageVisible.value = true;
    return;
  }, 1000);
}

function mouseLeave() {
  if (!SearchingAndSuggestionUtil.shouldShowImgInHeader(fieldUtil.referencedEntityType.value))
    return;
  mouseIn = false;
}

function initFilter() {
  shouldFilter.value = true;
  if (!popupVisible.value) {
    input.value!.updateInputValue(fieldUtil.inputFieldValueRef.value || '', false);
    input.value!.showPopup();
  }
}

async function onDownHandler() {
  initFilter();
}

function onF4KeyClicked(event: KeyboardEvent) {
  onBrowseClicked(event);
}

function onPopupShow() {
  popupVisible.value = true;
}

function onPopupHide() {
  popupVisible.value = false;
}

async function onBlur() {
  await fieldUtil.onBlur();
  shouldFilter.value = false;
}

async function onEnterKeyDown(e: KeyboardEvent, doNotGoToNextWithEnter?: boolean) {
  if (popupVisible.value || doNotGoToNextWithEnter)
    return;
  await fieldUtil.onBlur();
  emit("navigateToNext", {keyboardEvent: e});
}

function focus() {
  focusAndSelectInput(input);
}

function canReceiveFocus() {
  return !fieldUtil.isFieldDisabled(props.forceEnableField);
}

defineExpose({focus, canReceiveFocus})

watch([fieldUtil.isFieldFocused, useNamaBreakpoints().isMobile], ([isFocused, isMobileValue]) => {
  if (!isFocused || !isMobileValue)
    return;
  initFilter()
})

</script>

<template>
  <WidgetContainer :field="field" :entityType="presenter.entityType" :isSpanned="isSpanned"
                   :isFieldDisabled="fieldUtil.isFieldDisabled(forceEnableField)" :isInsideComposite="isInsideComposite"
                   :isFieldRequired="fieldUtil.isFieldRequired()" :doNotApplyThemeLayouts="doNotApplyThemeLayouts">
    <template #icon>
      <NamaIcon :block="field" :presenter="presenter" class="tw-size-[22px] tw-text-[22px]"/>
    </template>
    <template v-slot:widget="{ showAsPassword, doNotGoToNextWithEnter }">
      <q-select @keydown.enter="(e) => onEnterKeyDown(e, doNotGoToNextWithEnter)"
        :readonly="fieldUtil.isFieldDisabled(forceEnableField)"
        :type="showAsPassword ? 'password' : 'text'"
        outlined use-input hide-selected fill-input dense hide-bottom-space hide-dropdown-icon options-dense
        @filter="onFilter"
        @keydown.down="onDownHandler"
                @keydown.f4.stop="onBrowseClicked"
                @keydown.f6.stop="onBrowseClicked"
                @keydown.f7.stop.prevent="onBrowseClicked"
                @keydown.f8.stop="onBrowseClicked"
        @dblclick="onDownHandler"
        @popup-hide="onPopupHide"
        @popup-show="onPopupShow"
        input-debounce="0"
        :model-value="formattedValue"
        :loading="loading"
        :options="options"
        option-label="code"
        ref="input"
        @input-value="fieldUtil.onPartialUserInput"
        @update:model-value="fieldUtil.onSelect"
        @blur="onBlur"
        @focus="fieldUtil.onFocus"
        :error="!!props.presenter.fetchFieldErrorMsg(fieldId)"
        :style="{contain: isInsideComposite ? 'inline-size' : 'none'}"
        :placeholder="customPlaceHolder?customPlaceHolder:''"
        class="nama-customize-dense-input tw-w-full"
        :class="{'nama-field-input-disabled':fieldUtil.isFieldDisabled(forceEnableField),'nama-field-input':!fieldUtil.isFieldDisabled(forceEnableField),
        'nama-extra-criteria-field-input':isExtraCriteria}"
        input-class="nama-input-class"
        popup-content-class="!tw-max-h-[30vh] tw-font-normal dark:tw-text-white dark:tw-bg-namaBackground-bright-dark ag-custom-component-popup">
        <template v-slot:append>
          <template v-if="fetchCurrentValue() && fetchCurrentValue().id && hasImageOrDescriptor() && !isGridColumnFilterField">
            <div class="tw-max-w-full tw-flex tw-items-center tw-z-10" @mouseenter="mouseEnter" @mouseleave="mouseLeave" tabindex="-1">
              <a tabindex="-1" :href="PlaceUtils.linkToObject(fetchCurrentValue())" @click.stop
                 class="nama-btn-hover tw-cursor-pointer tw-h-7 compact:tw-h-5 ultra:tw-h-4 tw-bg-namaBackground-dim-light hover:tw-bg-gray-200 tw-rounded
                 dark:tw-bg-gray-600 dark:hover:tw-bg-gray-500 tw-text-namaFontColor-input-light dark:tw-text-namaFontColor-input-dark">
                <div
                  class="tw-flex horizontal:tw-max-w-[20vw] horizontal:quad:tw-max-w-[8vw] quad:tw-max-w-[12vw] horizontal:mobile:tw-max-w-[40vw] tw-items-center tw-h-full tw-justify-between tw-gap-2"
                  :class="{'tw-pe-1 ':hasColorOrImg(fetchCurrentValue()),'tw-px-1.5':!hasColorOrImg(fetchCurrentValue()), ' laptop:horizontal:quad:tw-max-w-[3vw] horizontal:quad:tw-max-w-[4vw]':isInsideComposite, 'laptop:horizontal:quad:tw-max-w-[8vw]':!isInsideComposite}">
                  <q-avatar round class="tw-size-6 compact:tw-size-5 ultra:tw-size-4" v-if="hasColorOrImg(fetchCurrentValue())"
                            :style="fetchCurrentValue().colorCode?'backgroundColor:'+fetchCurrentValue().colorCode:''">
                    <img class="tw-w-full tw-h-full" v-if="SearchingAndSuggestionUtil.shouldShowImgInHeader(fieldUtil.referencedEntityType.value)"
                         :src="AttachmentLinkUtil.downloadURLByRecordId(fetchCurrentValue(), DownloadPurpose.THUMBNAIL, false)"/>
                  </q-avatar>
                  <div class="tw-line-clamp-1 tw-font-medium tw-text-sm" v-if="SearchingAndSuggestionUtil.hasDescriptor(fetchCurrentValue())"
                       :title="SearchingAndSuggestionUtil.createOptionCaption(fetchCurrentValue())"
                       v-text="SearchingAndSuggestionUtil.createOptionCaption(fetchCurrentValue())"/>
                </div>
              </a>
            </div>
          </template>
          <template v-else-if="fetchCurrentValue() && fetchCurrentValue().id">
            <q-avatar
              tabindex="-1"
              square class="nama-avatar-hover tw-size-7 tw-cursor-pointer"
              :title="Translator.translateId('viewRecord')">
              <a :href="PlaceUtils.linkToObject(fetchCurrentValue())" @click.stop tabindex="-1">
                <LinkIcon class="tw-w-5 tw-h-5 nama-widget-icon-hover nama-widget-icon"/>
              </a>
            </q-avatar>
          </template>
          <div class="tw-flex tw-items-center tw-gap-1">
            <q-avatar
              square v-if="!formattedValue"
              @click.stop="(event)=>onBrowseClicked(event,true)"
              class="nama-avatar-hover tw-size-7 tw-cursor-pointer">
              <NamaIcon default-icon="nm-reply" class="tw-size-4 active-primary"/>
            </q-avatar>
            <q-avatar
              :title="`Open in new tab [F6] / [Ctrl+Click]\nEdit in Popup [F7] / [Alt+Click]\nNew [F8]`" square
              @click.stop="onBrowseClicked" class="nama-avatar-hover tw-size-7 tw-cursor-pointer">
              <AdvancedSearchIcon class="tw-size-4 nama-widget-icon-hover nama-widget-icon"/>
            </q-avatar>
          </div>
        </template>
        <template v-slot:option="scope">
          <q-item v-bind="scope.itemProps"
                  :class="{'tw-text-base tw-bg-primary-light dark:tw-bg-primary-dark tw-text-white dark:tw-text-gray-800':
                              fetchCurrentValue() && fetchCurrentValue().id === scope.opt.id}">
            <q-item-section v-bind:dir="scope.opt.optionDirection">
              <q-item-label v-text="scope.opt.optionText"/>
              <q-item-label caption class="dark:tw-text-gray-400 tw-font-medium tw-text-sm"
                            :class="{'dark:tw-text-gray-600': fetchCurrentValue() && fetchCurrentValue().id === scope.opt.id}"
                            v-if="SearchingAndSuggestionUtil.hasDescriptor(scope.opt as EntityReferenceData)"
                            v-text="scope.opt.optionCaption"/>
            </q-item-section>
            <q-item-section avatar v-if="scope.opt.optionHasColorOrImg">
              <q-avatar round class="tw-size-6 compact:tw-size-5 ultra:tw-size-4"
                        :style="scope.opt.colorCode?'backgroundColor:'+scope.opt.colorCode:''">
                <img class="tw-w-full tw-h-full" v-if="SearchingAndSuggestionUtil.shouldShowImgInSuggest(fieldUtil.referencedEntityType.value)"
                     :src="AttachmentLinkUtil.downloadURLByRecordId(scope.opt, DownloadPurpose.THUMBNAIL, false)"/>
              </q-avatar>
            </q-item-section>
          </q-item>
        </template>
        <ErrorTooltip v-if="presenter.fetchFieldErrorMsg(fieldId)" :error-msg="presenter.fetchFieldErrorMsg(fieldId)"/>
      </q-select>
      <ImagePreviewer v-if="fetchCurrentValue()" v-model="imageVisible"
                      :src="AttachmentLinkUtil.downloadURLByRecordId(fetchCurrentValue(), DownloadPurpose.THUMBNAIL, false)"/>
    </template>
  </WidgetContainer>
</template>

<style>

</style>
